/* CreditPlus-Finanzierung: CTA "ab X €/Monat" + Ratenrechner-Overlay.
 *
 * Datenquelle AUSSCHLIESSLICH /apps/finance/rates (App Proxy) — es wird
 * NIEMALS clientseitig gerechnet (CreditPlus-Berechnung weicht von der
 * Standard-Annuitätsformel ab, eigene Werte wären falsche Werbung).
 *
 * "available": false oder Fetch-Fehler → komplette Komponente ausblenden,
 * kein Fehler-UI. Variantenwechsel (Theme-Event on:variant:change) lädt
 * die Raten für den neuen Preis nach.
 */
(() => {
  const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
  const PCT = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  class CreditplusFinancing extends HTMLElement {
    connectedCallback() {
      this.row = this.querySelector('[data-cp-row]');
      this.contentEl = this.querySelector('[data-cp-content]');
      this.ctaBtn = this.querySelector('[data-cp-cta]');
      this.cheapestEl = this.querySelector('[data-cp-cheapest]');
      this.termSelect = this.querySelector('[data-cp-term]');
      this.applyLink = this.querySelector('[data-cp-apply]');
      this.exampleBlock = this.querySelector('[data-cp-example]');
      this.exampleText = this.querySelector('[data-cp-example-text]');
      this.lastRateRow = this.querySelector('[data-cp-lastrate-row]');
      this.variantIds = (this.dataset.variantIds || '').split(',');
      this.terms = [];

      this.onVariantChange = this.onVariantChange.bind(this);
      document.addEventListener('on:variant:change', this.onVariantChange);
      this.termSelect.addEventListener('change', () => this.renderTerm(this.termSelect.value));

      // Theme-Eigenheit: trapFocus() fokussiert das nicht fokussierbare
      // <modal-dialog> — ohne Fokus im Dialog greifen weder Trap noch
      // ESC-Listener. Deshalb bei jedem Oeffnen das Dialog-Fenster
      // (tabindex="-1") fokussieren. MutationObserver auf das open-
      // Attribut statt Klick-Listener: deterministisch, unabhaengig
      // von Listener-Reihenfolge und Timing. Fokus-Rueckgabe an die
      // CTA macht Modal.close() ueber openedBy.
      this.modalEl = this.querySelector('modal-dialog');
      this.modalWindow = this.querySelector('.modal__window');
      this.openObserver = new MutationObserver(() => {
        if (this.modalEl.hasAttribute('open')) this.modalWindow.focus();
      });
      this.openObserver.observe(this.modalEl, { attributes: true, attributeFilter: ['open'] });

      // Die Kaufbereich-Spalte ist position:sticky (stick_on_scroll) und
      // bildet einen Stacking-Context — ein fixed Modal darin liegt UNTER
      // Galerie-Elementen. Daher das Modal nach document.body verschieben
      // (die oben gegriffenen Referenzen bleiben gueltig).
      document.body.appendChild(this.modalEl);

      this.load(Number(this.dataset.priceCents));
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this.onVariantChange);
      if (this.openObserver) this.openObserver.disconnect();
      if (this.modalEl) this.modalEl.remove(); // nach body portiert — mit aufraeumen
    }

    onVariantChange(evt) {
      const variant = evt.detail && evt.detail.variant;
      if (!variant || !this.variantIds.includes(String(variant.id))) return;
      this.dataset.variantId = variant.id;
      if (!variant.available) {
        this.hide();
        return;
      }
      this.load(variant.price);
    }

    load(priceCents) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.fetchRates(priceCents), 150);
    }

    async fetchRates(priceCents) {
      const price = (priceCents / 100).toFixed(2);
      this.price = price;
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      try {
        const res = await fetch(`/apps/finance/rates?price=${price}`, {
          signal: this.abortController.signal
        });
        if (!res.ok) throw new Error(`rates ${res.status}`);
        const data = await res.json();
        if (price !== this.price) return; // veraltete Antwort
        if (!data.available || !Array.isArray(data.terms) || !data.terms.length || !data.cheapest) {
          this.hide();
          return;
        }
        this.render(data);
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.hide();
      }
    }

    hide() {
      // Nur den Box-Inhalt verbergen — die Zeile behaelt ihre reservierte
      // Hoehe, sonst entsteht bei available:false nach dem First Paint
      // ein Layout-Shift (CLS). Sichtbar ist so oder so nichts.
      this.contentEl.hidden = true;
    }

    render(data) {
      this.terms = data.terms.slice().sort((a, b) => a.duration - b.duration);

      // Ratenkauf-Box
      this.cheapestEl.textContent = EUR.format(data.cheapest.rate);
      this.contentEl.hidden = false;

      // Laufzeit-Auswahl: nur Laufzeiten aus /rates; gewählte Laufzeit
      // über einen Variantenwechsel hinweg beibehalten, sonst günstigste.
      const previous = this.termSelect.value;
      this.termSelect.innerHTML = '';
      this.terms.forEach((t) => {
        const opt = document.createElement('option');
        opt.value = String(t.duration);
        opt.textContent = `${t.duration} Monate`;
        this.termSelect.append(opt);
      });
      const selected = this.terms.some((t) => String(t.duration) === previous)
        ? previous
        : String(data.cheapest.duration);
      this.termSelect.value = selected;
      this.renderTerm(selected);
      this.renderExample();

      this.applyLink.href = `/apps/finance?variant=${this.dataset.variantId}&price=${this.price}`;
    }

    renderTerm(duration) {
      const term = this.terms.find((t) => String(t.duration) === String(duration));
      if (!term) return;
      this.setValue('rate', EUR.format(term.rate));
      this.setValue('rateLength', String(term.rateLength));
      this.setValue('nominalInterest', `${PCT.format(term.nominalInterest)} %`);
      this.setValue('interest', `${PCT.format(term.interest)} %`);
      this.setValue('netAmount', EUR.format(term.netAmount));
      this.setValue('grossAmount', EUR.format(term.grossAmount));

      const hasLastRate = term.lastRate !== null && term.lastRate !== undefined;
      this.lastRateRow.hidden = !hasLastRate;
      if (hasLastRate) this.setValue('lastRate', EUR.format(term.lastRate));
    }

    // Repräsentatives Beispiel (PAngV): Laufzeit 60 Monate; falls für den
    // Betrag nicht angeboten, längste verfügbare Laufzeit. Werte 1:1 aus /rates.
    renderExample() {
      const example = this.terms.find((t) => t.duration === 60) || this.terms[this.terms.length - 1];
      if (!example) return;
      const lastRatePart = example.lastRate !== null && example.lastRate !== undefined
        ? ` (abweichende Schlussrate ${EUR.format(example.lastRate)})`
        : '';
      this.exampleText.textContent =
        `Nettodarlehensbetrag ${EUR.format(example.netAmount)}, Vertragslaufzeit ` +
        `${example.duration} Monate, ${example.rateLength} monatliche Raten à ` +
        `${EUR.format(example.rate)}${lastRatePart}, gebundener Sollzinssatz ` +
        `${PCT.format(example.nominalInterest)} % p. a., effektiver Jahreszins ` +
        `${PCT.format(example.interest)} % p. a., Gesamtbetrag ${EUR.format(example.grossAmount)}.`;
      this.exampleBlock.hidden = false;
    }

    setValue(key, text) {
      // Im Modal suchen — das haengt nach dem Portal an document.body
      const el = this.modalEl.querySelector(`[data-cp-val="${key}"]`);
      if (el) el.textContent = text;
    }
  }

  if (!customElements.get('creditplus-financing')) {
    customElements.define('creditplus-financing', CreditplusFinancing);
  }
})();
